<template>
  <div>
    <app-navigation />
    <main class="admin-page">
      <div class="admin-heading">
        <span class="admin-kicker">DNS ownership</span>
        <h1>Domains</h1>
        <p>
          Domain authority follows the owning site and is enforced by the
          backend.
        </p>
      </div>
      <div v-if="error" class="admin-alert bad">{{ error }}</div>
      <div v-if="notice" class="admin-alert good">{{ notice }}</div>
      <section class="admin-card">
        <label class="admin-check"
          ><input
            v-model="adminDomain"
            type="checkbox"
            @change="switchDomainMode"
          />
          Deliberately assign a hostname to this Admin console</label
        >
        <div v-if="adminDomain" class="admin-alert warn">
          This override is only for the Admin console’s own hostname. Never use
          it for a customer-facing content domain.
        </div>
        <label
          >{{ adminDomain ? "Admin console" : "Child content site"
          }}<select v-model="site" @change="load">
            <option
              v-for="child in claimableSites"
              :key="child.id"
              :value="child.id"
            >
              {{ child.name }}
            </option>
          </select></label
        >
        <div class="admin-context">
          <strong>{{ siteName }}</strong
          ><code>{{ site }}</code>
        </div>
        <form class="admin-row" @submit.prevent="claim">
          <label class="grow"
            >Hostname<input
              v-model.trim="hostname"
              placeholder="example.com"
              required /></label
          ><button class="admin-primary">Claim</button>
        </form>
      </section>
      <section class="admin-card">
        <h2>Site domains</h2>
        <div class="admin-list">
          <article v-for="domain in domains" :key="domain.id">
            <div>
              <strong>{{ domain.hostname }}</strong
              ><small
                >{{ domain.status }} · TXT _cwcore-verify.{{
                  domain.hostname
                }}</small
              >
            </div>
            <button
              v-if="domain.status !== 'verified'"
              class="admin-secondary"
              @click="open(domain)"
            >
              DNS setup</button
            ><button class="admin-danger" @click="release(domain)">
              Release
            </button>
          </article>
          <p v-if="!domains.length">No domains belong to this site.</p>
        </div>
      </section>
      <div v-if="modal" class="admin-modal" @click.self="modal = null">
        <section class="admin-card">
          <h2>Verify {{ modal.hostname }}</h2>
          <p>Add this TXT record, wait for DNS propagation, then verify.</p>
          <dl>
            <dt>Name</dt>
            <dd>
              <code>_cwcore-verify.{{ modal.hostname }}</code>
            </dd>
            <dt>Value</dt>
            <dd>
              <code>{{ modal.verification_token }}</code>
            </dd>
            <dt>A</dt>
            <dd><code>54.146.234.9</code></dd>
            <dt>AAAA</dt>
            <dd><code>2600:1f18:7c4:c900:77fe:9910:14e7:4d74</code></dd>
          </dl>
          <div class="admin-row">
            <button class="admin-secondary" @click="modal = null">Cancel</button
            ><button class="admin-primary" @click="verify">Verify DNS</button>
          </div>
        </section>
      </div>
    </main>
  </div>
</template>
<script>
import { session } from "../platform/session";
import * as api from "../platform/api";
export default {
  name: "Domains",
  data() {
    return {
      root: session.root,
      children: [],
      site: "",
      adminDomain: false,
      domains: [],
      hostname: "",
      modal: null,
      error: "",
      notice: "",
    };
  },
  computed: {
    claimableSites() {
      return this.adminDomain
        ? [this.root]
        : this.children.filter((site) => !site.is_admin);
    },
    siteName() {
      return (
        [this.root, ...this.children].find((v) => v.id === this.site)?.name ||
        this.site
      );
    },
  },
  async created() {
    this.children = await api.children(this.root.id, this.root.id);
    this.site = this.claimableSites[0]?.id || "";
    if (this.site) await this.load();
  },
  methods: {
    async perform(fn, message) {
      this.error = "";
      this.notice = "";
      try {
        await fn();
        this.notice = message;
      } catch (e) {
        this.error =
          e.code === "admin_site_domain_requires_override" && !this.adminDomain
            ? "Wrong site selected: customer domains must be claimed on the child content site. Select that child and retry."
            : e.message;
      }
    },
    async route(siteId = this.site) {
      try {
        await api.publishRouting(siteId, this.root.id);
      } catch (e) {
        if (e.status !== 409) throw e;
      }
    },
    async load() {
      if (!this.site) {
        this.domains = [];
        return;
      }
      await this.perform(async () => {
        this.domains = await api.domains(this.site, this.root.id);
      }, "Domains loaded");
    },
    async claim() {
      await this.perform(async () => {
        this.modal = await api.claimDomain(
          this.site,
          this.root.id,
          this.hostname,
          this.adminDomain
        );
        this.hostname = "";
        await this.load();
      }, "Domain claimed");
    },
    async switchDomainMode() {
      this.site = this.claimableSites[0]?.id || "";
      this.domains = [];
      if (this.site) await this.load();
    },
    open(domain) {
      this.modal = domain;
    },
    async verify() {
      await this.perform(async () => {
        const ownerSiteId = this.modal.site_id;
        await api.verifyDomain(ownerSiteId, this.root.id, this.modal.id);
        await this.route(ownerSiteId);
        this.modal = null;
        await this.load();
      }, "Domain verified and routing publication queued. Seed initial content explicitly from Content.");
    },
    async release(domain) {
      if (
        !confirm(`Release ${domain.hostname} and publish the routing change?`)
      )
        return;
      await this.perform(async () => {
        await api.releaseDomain(domain.site_id, this.root.id, domain.id);
        await this.route(domain.site_id);
        await this.load();
      }, "Domain released and routing publication queued");
    },
  },
};
</script>
